import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import PageHeader from '../../components/PageHeader';
import {
  useRedbeatSchedulesQuery,
  useCreateRedbeatScheduleMutation,
  useUpdateRedbeatScheduleMutation,
  useDeleteRedbeatScheduleMutation,
  useCrawlQueueEntriesQuery,
  useCrawlQueueStatsQuery,
  useCortexConfigQuery,
  useUpdateCrawlQueueConfigMutation,
  useLockCrawlQueueEntriesMutation,
  useUnlockCrawlQueueEntriesMutation,
  useDeleteCrawlQueueEntriesMutation,
} from '../../gql/generated/graphql';
import { useSnackbar } from '../../providers/SnackbarProvider';

type ScheduleEntry = {
  name: string;
  task: string;
  enabled: boolean;
  scheduleType: string;
  intervalSeconds?: number | null;
  crontabMinute?: string | null;
  crontabHour?: string | null;
  crontabDayOfWeek?: string | null;
  crontabDayOfMonth?: string | null;
  queue?: string | null;
  kwargs?: any;
  totalRunCount?: number | null;
  lastRunAt?: string | null;
};

type DialogForm = {
  name: string;
  task: string;
  scheduleType: 'interval' | 'crontab';
  intervalSeconds: string;
  crontabMinute: string;
  crontabHour: string;
  crontabDayOfWeek: string;
  crontabDayOfMonth: string;
  queue: string;
  kwargs: string;
  enabled: boolean;
};

const emptyForm: DialogForm = {
  name: '',
  task: '',
  scheduleType: 'crontab',
  intervalSeconds: '120',
  crontabMinute: '*',
  crontabHour: '*',
  crontabDayOfWeek: '*',
  crontabDayOfMonth: '*',
  queue: 'quick_queue',
  kwargs: '{}',
  enabled: true,
};

const QUEUE_OPTIONS = ['quick_queue', 'crawl_long_queue', 'long_queue', 'serializer_queue'];

/** Validate a single crontab field against Celery's parser rules. */
function validateCronField(value: string, field: 'minute' | 'hour' | 'day_of_week' | 'day_of_month'): string | null {
  if (!value || value.trim() === '') return 'Required';
  const v = value.trim();
  const ranges: Record<string, { min: number; max: number; names?: string[] }> = {
    minute: { min: 0, max: 59 },
    hour: { min: 0, max: 23 },
    day_of_week: { min: 0, max: 6, names: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] },
    day_of_month: { min: 1, max: 31 },
  };
  const { min, max, names } = ranges[field];
  // Accept * alone
  if (v === '*') return null;
  // Split on comma for lists like "1,3,5"
  for (const part of v.split(',')) {
    const p = part.trim();
    // */N or N-M/N
    const stepMatch = p.match(/^(\*|\d+(?:-\d+)?)\/(\d+)$/);
    if (stepMatch) {
      const step = parseInt(stepMatch[2]);
      if (step < 1) return `Step must be >= 1 in "${p}"`;
      if (stepMatch[1] !== '*') {
        // validate the range part
        const rangeErr = validateRange(stepMatch[1], min, max, names);
        if (rangeErr) return rangeErr;
      }
      continue;
    }
    // N-M range
    if (p.includes('-')) {
      const rangeErr = validateRange(p, min, max, names);
      if (rangeErr) return rangeErr;
      continue;
    }
    // Single value
    const numErr = validateSingleValue(p, min, max, names);
    if (numErr) return numErr;
  }
  return null;
}

function validateRange(p: string, min: number, max: number, names?: string[]): string | null {
  const parts = p.split('-');
  if (parts.length !== 2) return `Invalid range "${p}"`;
  for (const v of parts) {
    const err = validateSingleValue(v.trim(), min, max, names);
    if (err) return err;
  }
  return null;
}

function validateSingleValue(v: string, min: number, max: number, names?: string[]): string | null {
  if (names && names.includes(v.toLowerCase())) return null;
  const num = parseInt(v);
  if (isNaN(num)) return `"${v}" is not a valid number${names ? ' or name (' + names.join(', ') + ')' : ''}`;
  if (num < min || num > max) return `${num} is out of range (${min}-${max})`;
  return null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scheduled-tasks-tabpanel-${index}`}
      aria-labelledby={`scheduled-tasks-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function formatSchedule(entry: ScheduleEntry): string {
  if (entry.scheduleType === 'interval' && entry.intervalSeconds) {
    const secs = entry.intervalSeconds;
    if (secs >= 3600) return `Every ${(secs / 3600).toFixed(1)}h`;
    if (secs >= 60) return `Every ${Math.round(secs / 60)}m`;
    return `Every ${secs}s`;
  }
  if (entry.scheduleType === 'crontab') {
    const parts = [
      entry.crontabMinute ?? '*',
      entry.crontabHour ?? '*',
      entry.crontabDayOfMonth ?? '*',
      '*',
      entry.crontabDayOfWeek ?? '*',
    ];
    return parts.join(' ');
  }
  return 'Unknown';
}

type CrawlQueueFilterTab = 'all' | 'pending' | 'locked' | 'started' | 'errored';

const ScheduledTasksPage = () => {
  const { showSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<DialogForm>(emptyForm);

  // ---- Schedules tab ----
  const { data: schedulesData, loading, refetch } = useRedbeatSchedulesQuery({ fetchPolicy: 'network-only' });
  const [createSchedule] = useCreateRedbeatScheduleMutation();
  const [updateSchedule] = useUpdateRedbeatScheduleMutation();
  const [deleteSchedule] = useDeleteRedbeatScheduleMutation();

  // ---- Crawl Queue tab ----
  const [queueTab, setQueueTab] = useState<CrawlQueueFilterTab>('all');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [configDraft, setConfigDraft] = useState<{
    enabled?: boolean;
    batchSize?: number;
    intervalSeconds?: number;
  }>({});
  const [configDirty, setConfigDirty] = useState(false);

  const queueFilterVars = (() => {
    switch (queueTab) {
      case 'pending':
        return { crawlStarted: false, locked: false, hasError: false };
      case 'locked':
        return { locked: true };
      case 'started':
        return { crawlStarted: true };
      case 'errored':
        return { hasError: true };
      default:
        return {};
    }
  })();

  const {
    data: entriesData,
    loading: entriesLoading,
    refetch: refetchEntries,
  } = useCrawlQueueEntriesQuery({
    variables: { ...queueFilterVars, limit: 500, offset: 0 },
    fetchPolicy: 'network-only',
    skip: tabValue !== 1,
  });

  const { data: statsData, refetch: refetchStats } = useCrawlQueueStatsQuery({
    fetchPolicy: 'network-only',
    skip: tabValue !== 1,
  });

  useCortexConfigQuery({
    onCompleted: data => {
      if (data?.cortexConfig) {
        setConfigDraft({
          enabled: data.cortexConfig.crawlQueueEnabled ?? true,
          batchSize: data.cortexConfig.crawlQueueBatchSize ?? 5,
          intervalSeconds: data.cortexConfig.crawlQueueIntervalSeconds ?? 120,
        });
      }
    },
  });

  const [updateConfig] = useUpdateCrawlQueueConfigMutation();
  const [lockEntries] = useLockCrawlQueueEntriesMutation();
  const [unlockEntries] = useUnlockCrawlQueueEntriesMutation();
  const [deleteEntries] = useDeleteCrawlQueueEntriesMutation();

  // ---- Schedule handlers ----

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (entry: ScheduleEntry) => {
    setForm({
      name: entry.name,
      task: entry.task,
      scheduleType: (entry.scheduleType as 'interval' | 'crontab') || 'crontab',
      intervalSeconds: String(entry.intervalSeconds ?? 120),
      crontabMinute: entry.crontabMinute ?? '*',
      crontabHour: entry.crontabHour ?? '*',
      crontabDayOfWeek: entry.crontabDayOfWeek ?? '*',
      crontabDayOfMonth: entry.crontabDayOfMonth ?? '*',
      queue: entry.queue ?? 'quick_queue',
      kwargs: entry.kwargs ? JSON.stringify(entry.kwargs) : '{}',
      enabled: entry.enabled,
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDeleteSchedule = async (name: string) => {
    if (!window.confirm(`Delete schedule "${name}"?`)) return;
    try {
      await deleteSchedule({ variables: { name } });
      showSnackbar(`Deleted schedule "${name}"`, 'success');
      refetch();
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const cronErrors = {
    minute: form.scheduleType === 'crontab' ? validateCronField(form.crontabMinute, 'minute') : null,
    hour: form.scheduleType === 'crontab' ? validateCronField(form.crontabHour, 'hour') : null,
    dayOfWeek: form.scheduleType === 'crontab' ? validateCronField(form.crontabDayOfWeek, 'day_of_week') : null,
    dayOfMonth: form.scheduleType === 'crontab' ? validateCronField(form.crontabDayOfMonth, 'day_of_month') : null,
  };
  const hasCronErrors = form.scheduleType === 'crontab' && Object.values(cronErrors).some(e => e !== null);

  const handleSaveSchedule = async () => {
    try {
      if (hasCronErrors) {
        const firstError = Object.entries(cronErrors).find(([, v]) => v !== null);
        showSnackbar(`Invalid crontab: ${firstError?.[0]} — ${firstError?.[1]}`, 'error');
        return;
      }

      let parsedKwargs: any = {};
      try {
        parsedKwargs = JSON.parse(form.kwargs);
      } catch {
        showSnackbar('Invalid JSON in kwargs', 'error');
        return;
      }

      const common = {
        task: form.task,
        scheduleType: form.scheduleType,
        intervalSeconds: form.scheduleType === 'interval' ? parseFloat(form.intervalSeconds) : undefined,
        crontabMinute: form.scheduleType === 'crontab' ? form.crontabMinute : undefined,
        crontabHour: form.scheduleType === 'crontab' ? form.crontabHour : undefined,
        crontabDayOfWeek: form.scheduleType === 'crontab' ? form.crontabDayOfWeek : undefined,
        crontabDayOfMonth: form.scheduleType === 'crontab' ? form.crontabDayOfMonth : undefined,
        queue: form.queue || undefined,
        kwargs: Object.keys(parsedKwargs).length > 0 ? JSON.stringify(parsedKwargs) : undefined,
      };

      if (isEditing) {
        const result = await updateSchedule({
          variables: { name: form.name, ...common, enabled: form.enabled },
        });
        if (result.errors?.length) {
          showSnackbar(`Failed to update schedule: ${result.errors[0].message}`, 'error');
          return;
        }
        showSnackbar(`Updated schedule "${form.name}"`, 'success');
      } else {
        const result = await createSchedule({
          variables: { name: form.name, ...common },
        });
        if (result.errors?.length) {
          showSnackbar(`Failed to create schedule: ${result.errors[0].message}`, 'error');
          return;
        }
        showSnackbar(`Created schedule "${form.name}"`, 'success');
      }

      setDialogOpen(false);
      refetch();
    } catch (e: any) {
      const msg = e?.graphQLErrors?.[0]?.message || e?.message || 'Unknown error';
      showSnackbar(`Failed to save schedule: ${msg}`, 'error');
    }
  };

  // ---- Crawl Queue handlers ----

  const handleSaveQueueConfig = async () => {
    try {
      await updateConfig({ variables: configDraft });
      showSnackbar('Crawl queue config updated', 'success');
      setConfigDirty(false);
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleLock = async () => {
    try {
      const result = await lockEntries({ variables: { ids: selectedIds } });
      const count = result.data?.lockCrawlQueueEntries?.updatedCount ?? 0;
      showSnackbar(`Locked ${count} entries`, 'success');
      setSelectedIds([]);
      refetchEntries();
      refetchStats();
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleUnlock = async () => {
    try {
      const result = await unlockEntries({ variables: { ids: selectedIds } });
      const count = result.data?.unlockCrawlQueueEntries?.updatedCount ?? 0;
      showSnackbar(`Unlocked ${count} entries`, 'success');
      setSelectedIds([]);
      refetchEntries();
      refetchStats();
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleDeleteQueueEntries = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} entries? This cannot be undone.`)) return;
    try {
      const result = await deleteEntries({ variables: { ids: selectedIds } });
      const count = result.data?.deleteCrawlQueueEntries?.deletedCount ?? 0;
      showSnackbar(`Deleted ${count} entries`, 'success');
      setSelectedIds([]);
      refetchEntries();
      refetchStats();
    } catch (e: any) {
      showSnackbar(e.message, 'error');
    }
  };

  const handleRefreshQueue = () => {
    refetchEntries();
    refetchStats();
  };

  // ---- Column definitions ----

  const scheduleColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 220 },
    { field: 'task', headerName: 'Task Path', flex: 1, minWidth: 300 },
    {
      field: 'schedule',
      headerName: 'Schedule',
      width: 180,
      valueGetter: (_value: any, row: ScheduleEntry) => formatSchedule(row),
    },
    { field: 'queue', headerName: 'Queue', width: 140 },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 90,
      renderCell: params => (
        <Chip label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} size="small" />
      ),
    },
    {
      field: 'lastRunAt',
      headerName: 'Last Run',
      width: 170,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleString() : 'Never'),
    },
    { field: 'totalRunCount', headerName: 'Runs', width: 80, type: 'number' },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: params => (
        <Stack direction="row">
          <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteSchedule(params.row.name)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const queueColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'domain', headerName: 'Domain', flex: 1, minWidth: 200 },
    { field: 'companyName', headerName: 'Company', flex: 1, minWidth: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      valueGetter: (_value: any, row: any) => {
        if (row.errorMessage) return 'Error';
        if (row.crawlStarted) return 'Started';
        if (row.locked) return 'Locked';
        return 'Pending';
      },
      renderCell: params => {
        const colorMap: Record<string, 'error' | 'success' | 'warning' | 'default'> = {
          Error: 'error',
          Started: 'success',
          Locked: 'warning',
          Pending: 'default',
        };
        return <Chip label={params.value} color={colorMap[params.value as string] ?? 'default'} size="small" />;
      },
    },
    { field: 'triggeredBy', headerName: 'Triggered By', width: 140 },
    {
      field: 'datetimeCreated',
      headerName: 'Created',
      width: 170,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'errorMessage',
      headerName: 'Error',
      flex: 1,
      minWidth: 200,
      renderCell: params =>
        params.value ? (
          <Tooltip title={params.value}>
            <Typography variant="body2" color="error" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        ) : null,
    },
  ];

  const scheduleRows = schedulesData?.redbeatSchedules ?? [];
  const queueRows = entriesData?.crawlQueueEntries ?? [];
  const stats = statsData?.crawlQueueStats;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="Scheduled Tasks" />

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_e, newValue) => setTabValue(newValue)} aria-label="scheduled tasks tabs">
          <Tab label="Schedules" />
          <Tab label="Crawl Queue" />
        </Tabs>
      </Paper>

      {/* ---- Tab 0: Schedules ---- */}
      <TabPanel value={tabValue} index={0}>
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Schedule
          </Button>
        </Stack>

        <DataGrid
          rows={scheduleRows}
          columns={scheduleColumns}
          loading={loading}
          getRowId={row => row.name}
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ height: 600 }}
        />
      </TabPanel>

      {/* ---- Tab 1: Crawl Queue ---- */}
      <TabPanel value={tabValue} index={1}>
        {/* Config Bar */}
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={configDraft.enabled ?? true}
                  onChange={e => {
                    setConfigDraft(prev => ({ ...prev, enabled: e.target.checked }));
                    setConfigDirty(true);
                  }}
                />
              }
              label="Queue Enabled"
            />
            <TextField
              label="Batch Size"
              type="number"
              size="small"
              value={configDraft.batchSize ?? ''}
              onChange={e => {
                setConfigDraft(prev => ({ ...prev, batchSize: parseInt(e.target.value) || 0 }));
                setConfigDirty(true);
              }}
              sx={{ width: 120 }}
            />
            <TextField
              label="Interval (s)"
              type="number"
              size="small"
              value={configDraft.intervalSeconds ?? ''}
              onChange={e => {
                setConfigDraft(prev => ({ ...prev, intervalSeconds: parseInt(e.target.value) || 0 }));
                setConfigDirty(true);
              }}
              sx={{ width: 120 }}
            />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!configDirty}
              onClick={handleSaveQueueConfig}
            >
              Save
            </Button>
          </Stack>
        </Box>

        {/* Stats Chips */}
        {stats && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip label={`Total: ${stats.total}`} />
            <Chip label={`Pending: ${stats.pending}`} color="default" />
            <Chip label={`Locked: ${stats.locked}`} color="warning" />
            <Chip label={`Started: ${stats.started}`} color="success" />
            <Chip label={`Errored: ${stats.errored}`} color="error" />
          </Stack>
        )}

        {/* Filter Tabs + Refresh */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Tabs
            value={queueTab}
            onChange={(_e, v) => {
              setQueueTab(v);
              setSelectedIds([]);
            }}
          >
            <Tab label="All" value="all" />
            <Tab label="Pending" value="pending" />
            <Tab label="Locked" value="locked" />
            <Tab label="Started" value="started" />
            <Tab label="Errored" value="errored" />
          </Tabs>
          <IconButton onClick={handleRefreshQueue}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {/* Batch Toolbar */}
        {selectedIds.length > 0 && (
          <Toolbar variant="dense" sx={{ mb: 1, bgcolor: 'action.selected', borderRadius: 1, gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {selectedIds.length} selected
            </Typography>
            <Button size="small" startIcon={<LockIcon />} onClick={handleLock}>
              Lock
            </Button>
            <Button size="small" startIcon={<LockOpenIcon />} onClick={handleUnlock}>
              Unlock
            </Button>
            <Button size="small" startIcon={<DeleteOutlineIcon />} color="error" onClick={handleDeleteQueueEntries}>
              Delete
            </Button>
          </Toolbar>
        )}

        {/* DataGrid */}
        <DataGrid
          rows={queueRows}
          columns={queueColumns}
          loading={entriesLoading}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(ids: any) => setSelectedIds(ids as number[])}
          getRowId={row => row.id}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ height: 600 }}
        />
      </TabPanel>

      {/* ---- Create / Edit Schedule Dialog ---- */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              disabled={isEditing}
              fullWidth
            />
            <TextField
              label="Task Path"
              value={form.task}
              onChange={e => setForm(prev => ({ ...prev, task: e.target.value }))}
              placeholder="project.tasks.cron_tasks.process_crawl_queue"
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Schedule Type
              </Typography>
              <ToggleButtonGroup
                value={form.scheduleType}
                exclusive
                onChange={(_e, v) => v && setForm(prev => ({ ...prev, scheduleType: v }))}
                size="small"
              >
                <ToggleButton value="interval">Interval</ToggleButton>
                <ToggleButton value="crontab">Crontab</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {form.scheduleType === 'interval' ? (
              <TextField
                label="Interval (seconds)"
                type="number"
                value={form.intervalSeconds}
                onChange={e => setForm(prev => ({ ...prev, intervalSeconds: e.target.value }))}
                fullWidth
              />
            ) : (
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Minute"
                  placeholder="0-59, *, */N"
                  value={form.crontabMinute}
                  onChange={e => setForm(prev => ({ ...prev, crontabMinute: e.target.value }))}
                  size="small"
                  error={!!cronErrors.minute}
                  helperText={cronErrors.minute}
                />
                <TextField
                  label="Hour"
                  placeholder="0-23, *, */N"
                  value={form.crontabHour}
                  onChange={e => setForm(prev => ({ ...prev, crontabHour: e.target.value }))}
                  size="small"
                  error={!!cronErrors.hour}
                  helperText={cronErrors.hour}
                />
                <TextField
                  label="Day of Week"
                  placeholder="0-6, mon-sun, *, */N"
                  value={form.crontabDayOfWeek}
                  onChange={e => setForm(prev => ({ ...prev, crontabDayOfWeek: e.target.value }))}
                  size="small"
                  error={!!cronErrors.dayOfWeek}
                  helperText={cronErrors.dayOfWeek}
                />
                <TextField
                  label="Day of Month"
                  placeholder="1-31, *, */N"
                  value={form.crontabDayOfMonth}
                  onChange={e => setForm(prev => ({ ...prev, crontabDayOfMonth: e.target.value }))}
                  size="small"
                  error={!!cronErrors.dayOfMonth}
                  helperText={cronErrors.dayOfMonth}
                />
              </Stack>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Queue</InputLabel>
              <Select
                value={form.queue}
                label="Queue"
                onChange={e => setForm(prev => ({ ...prev, queue: e.target.value }))}
              >
                {QUEUE_OPTIONS.map(q => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="kwargs (JSON)"
              value={form.kwargs}
              onChange={e => setForm(prev => ({ ...prev, kwargs: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />

            {isEditing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
                  />
                }
                label="Enabled"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSchedule}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledTasksPage;
